// Feed subscriptions (RSS/Atom — YouTube channels/playlists, Twitch VODs, blogs).

import type {
  CreateFeedCategoryInput,
  CreateFeedInput,
  Feed,
  FeedCategory,
  FeedEntry,
  FeedValidateResult,
  FeedsUiState,
  UpdateFeedCategoryInput,
  UpdateFeedInput,
} from "@/feeds/model/wire.js";
import { api, polled } from "@/platform/client";
import { qk } from "@/platform/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// The server fetches feeds on its own schedule and announces each new entry
// over the notification stream, which refreshes these queries
// (`feedNotifications`). A tab that comes back reads them again at once.
// The poll catches what the stream does not announce: a fetch's status, and
// read state changed on another device.
const FEEDS_REFRESH_MS = 30_000;
const ON_RETURN = { refetchOnWindowFocus: "always" } as const;

function invalidateFeeds(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.feedCategories.all });
  qc.invalidateQueries({ queryKey: qk.feeds.all });
  qc.invalidateQueries({ queryKey: qk.feedEntries.all });
}

// ---- categories ----
export function useFeedCategories() {
  return useQuery({
    queryKey: qk.feedCategories.all,
    queryFn: () => api.get<FeedCategory[]>("/feeds/categories"),
    ...polled(FEEDS_REFRESH_MS),
    ...ON_RETURN,
  });
}

export function useCreateFeedCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFeedCategoryInput) =>
      api.post<FeedCategory>("/feeds/categories", input),
    onSuccess: () => invalidateFeeds(qc),
  });
}

export function useUpdateFeedCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateFeedCategoryInput }) =>
      api.patch<FeedCategory>(`/feeds/categories/${id}`, patch),
    onSuccess: () => invalidateFeeds(qc),
  });
}

export function useDeleteFeedCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/feeds/categories/${id}`),
    onSuccess: () => invalidateFeeds(qc),
  });
}

// ---- feeds ----
export function useFeeds() {
  return useQuery({
    queryKey: qk.feeds.all,
    queryFn: () => api.get<Feed[]>("/feeds"),
    ...polled(FEEDS_REFRESH_MS),
    ...ON_RETURN,
  });
}

export function useValidateFeed() {
  return useMutation({
    mutationFn: (url: string) => api.post<FeedValidateResult>("/feeds/validate", { url }),
  });
}

export function useCreateFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFeedInput) => api.post<Feed>("/feeds", input),
    onSuccess: () => invalidateFeeds(qc),
  });
}

export function useUpdateFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateFeedInput }) =>
      api.patch<Feed>(`/feeds/${id}`, patch),
    onSuccess: () => invalidateFeeds(qc),
  });
}

export function useDeleteFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/feeds/${id}`),
    onSuccess: () => invalidateFeeds(qc),
  });
}

export function useSyncAllFeeds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/feeds/sync-all"),
    onSettled: () => invalidateFeeds(qc),
  });
}

/** Marks sources "opened" — clears their freshness accent highlight (`hasNew`)
 *  until the next sync lands entries newer than this. Bulk: a single source is
 *  a one-element array. */
export function useMarkFeedsViewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feedIds: number[]) => api.post("/feeds/view-bulk", { feedIds }),
    onSuccess: () => invalidateFeeds(qc),
  });
}

// ---- entries ----
export interface FeedEntriesParams {
  categoryId?: number;
  /** Scope to one feed (single id) or merge across an arbitrary set (the Feeds
   *  page's "Uncategorized" bucket, which has no real categoryId). */
  feedIds?: number[];
  unreadOnly?: boolean;
  limit?: number;
}

function feedEntriesQueryOptions(params: FeedEntriesParams) {
  const key = [
    params.categoryId ?? null,
    params.feedIds?.join(",") ?? null,
    params.unreadOnly ?? null,
    params.limit ?? null,
  ];
  const search = new URLSearchParams();
  if (params.categoryId) search.set("categoryId", String(params.categoryId));
  if (params.feedIds?.length) search.set("feedIds", params.feedIds.join(","));
  if (params.unreadOnly) search.set("unreadOnly", "true");
  if (params.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return {
    queryKey: qk.feedEntries.list(key),
    queryFn: () => api.get<FeedEntry[]>(`/feeds/entries${qs ? `?${qs}` : ""}`),
  };
}

export function useFeedEntries(params: FeedEntriesParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    ...feedEntriesQueryOptions(params),
    ...polled(FEEDS_REFRESH_MS),
    enabled: options?.enabled,
    ...ON_RETURN,
  });
}

export function useMarkFeedEntryRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }: { id: number; read: boolean }) =>
      api.patch(`/feeds/entries/${id}`, { read }),
    onSuccess: () => invalidateFeeds(qc),
  });
}

/** Clears an entry's "new" accent without marking it read. */
export function useMarkFeedEntrySeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/feeds/entries/${id}/seen`),
    onSuccess: () => invalidateFeeds(qc),
  });
}

export function useMarkAllFeedEntriesRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scope: { feedIds?: number[]; categoryIds?: number[] }) =>
      api.post<{ updated: number }>("/feeds/entries/mark-all-read", scope),
    onSuccess: () => invalidateFeeds(qc),
  });
}

export function useDeleteFeedEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<{ deleted: number }>("/feeds/entries/delete", { ids }),
    onSuccess: () => invalidateFeeds(qc),
  });
}

// ---- preferences ----

export function useFeedPollInterval() {
  return useQuery({
    queryKey: qk.settings.feedPollInterval,
    queryFn: () => api.get<{ minutes: number }>("/settings/feed-poll-interval"),
  });
}

export function useUpdateFeedPollInterval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (minutes: number) =>
      api.put<{ minutes: number }>("/settings/feed-poll-interval", { minutes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.feedPollInterval }),
  });
}

export function useFeedAutoReadDelay() {
  return useQuery({
    queryKey: qk.settings.feedAutoReadDelay,
    queryFn: () => api.get<{ seconds: number }>("/settings/feed-auto-read-delay"),
  });
}

export function useUpdateFeedAutoReadDelay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seconds: number) =>
      api.put<{ seconds: number }>("/settings/feed-auto-read-delay", { seconds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.feedAutoReadDelay }),
  });
}

export function useFeedEntryRetentionDays() {
  return useQuery({
    queryKey: qk.settings.feedEntryRetentionDays,
    queryFn: () => api.get<{ days: number }>("/settings/feed-entry-retention-days"),
  });
}

export function useUpdateFeedEntryRetentionDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      api.put<{ days: number }>("/settings/feed-entry-retention-days", { days }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.feedEntryRetentionDays }),
  });
}

export function useFeedsUiState() {
  return useQuery({
    queryKey: qk.settings.feedsUiState,
    queryFn: () => api.get<FeedsUiState>("/settings/feeds-ui-state"),
  });
}

export function useUpdateFeedsUiState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (state: FeedsUiState) => api.put<FeedsUiState>("/settings/feeds-ui-state", state),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings.feedsUiState }),
  });
}
